export type LocalizationLocale = string;

/** Browser preference used before the attachment can start a response. */
export interface AttachmentLocale {
  locale: LocalizationLocale;
  source: 'explicit' | 'navigator';
}

export function readAttachmentLocale(query: URLSearchParams): AttachmentLocale | undefined {
  const locales = query.getAll('locale');
  const sources = query.getAll('localeSource');
  if (locales.length === 0 && sources.length === 0) {
    return undefined;
  }
  const locale = locales[0];
  const source = sources[0];
  if (
    locales.length !== 1 ||
    sources.length !== 1 ||
    locale.length > 128 ||
    (source !== 'explicit' && source !== 'navigator')
  ) {
    throw new Error('Invalid attachment locale.');
  }
  try {
    const canonical = Intl.getCanonicalLocales(locale.trim());
    if (canonical.length === 1) {
      return { locale: canonical[0], source };
    }
  } catch {}
  throw new Error('Invalid attachment locale.');
}

export function writeAttachmentLocale(query: URLSearchParams, candidate?: AttachmentLocale): void {
  query.delete('locale');
  query.delete('localeSource');
  if (candidate) {
    query.set('locale', candidate.locale);
    query.set('localeSource', candidate.source);
  }
}

export interface LocalizationBundle {
  locale: LocalizationLocale;
  messages: Record<string, string>;
}

export interface LocalizationBundleIdentity {
  catalogRevision: string;
  messageLocale: LocalizationLocale;
  sessionLocaleRevision: number;
}

export interface LocalizationOwnerOverlay {
  locale: LocalizationLocale;
  messages: Record<string, string>;
}

export interface LocalizationClientBuild {
  catalogRevision: string;
  sourceLocale: LocalizationLocale;
  bundles: LocalizationBundle[];
}

export interface LocalizationServerBuild extends LocalizationClientBuild {
  policyVersion: string;
  catalogJson: string;
  ownerOverlays: LocalizationOwnerOverlay[];
}

export interface LocalizationResolveRequest {
  catalogRevision: string;
  messageLocale: LocalizationLocale;
  policyVersion: string;
  catalogJson: string;
}

export interface LocalizationResolveReady {
  status: 'ready';
  catalogRevision: string;
  policyVersion: string;
  bundle: LocalizationBundle;
}

export type LocalizationFallbackReason =
  | 'busy'
  | 'rate-limited'
  | 'generation-failed'
  | 'storage-failed';

export interface LocalizationResolveSourceFallback {
  status: 'source-fallback';
  catalogRevision: string;
  messageLocale: LocalizationLocale;
  policyVersion: string;
  reason: LocalizationFallbackReason;
  retryAfterMs?: number;
}

export type LocalizationResolveResult =
  | LocalizationResolveReady
  | LocalizationResolveSourceFallback;
